import { useEffect, useMemo, useRef, useState } from 'react';
import { useDownloadExcel } from 'react-export-table-to-excel';
import { useDispatch, useSelector } from 'react-redux';
import { formatPrice } from '../../../../util/formatPrice';
import { ReportTableHeader } from '../../../uiComponents/ReportTableHeader';
import { stringDate, stringTime } from '../../../../util';
import { formatFileName } from '../../../../util/strings';
import { ExportButton } from '../../../uiComponents/ExportButton';
import moment from 'moment';
import { cosmetologicFeesByCosmetologistReportConst } from '../../../../constants';
import { CosmetologicFeesByCosmetologistFilter } from '../../ReportFilter/CosmetologicFeesByCosmetologistFilter';
import { DataNotFound } from '../DataNotFound';
import { clearCosmetologicFeesByComestologist } from '../../../../appRedux/actions/Reports';

export const CosmetologicFeesByCosmetologistReport = () => {
  const tableRef = useRef(null);
  const dispatch = useDispatch();

  const { cosmetologicFeesByCosmetologist } = useSelector(
    (state) => state.reports
  );
  const { fullname, role } = useSelector((state) => state.userAuth);
  const { cosmetologicFeesByCosmetologistFilters } = useSelector(
    (state) => state.individualReports
  );

  const [period, setPeriod] = useState();

  const { onDownload } = useDownloadExcel({
    currentTableRef: tableRef.current,
    filename: formatFileName(cosmetologicFeesByCosmetologistReportConst.title),
    sheet: cosmetologicFeesByCosmetologistReportConst.title,
  });

  const header = cosmetologicFeesByCosmetologistReportConst.header;

  const sales = cosmetologicFeesByCosmetologist?.sales || [];
  const globalPercent = Number(cosmetologicFeesByCosmetologist?.percent) || 0;
  const hasData = sales.length > 0;

  const { total, totalCommissions } = useMemo(() => {
    return sales.reduce(
      (acc, row) => {
        acc.total += Number(row.salePrice) || 0;
        acc.totalCommissions += Number(row.commission) || 0;
        return acc;
      },
      { total: 0, totalCommissions: 0 }
    );
  }, [sales]);

  useEffect(() => {
    dispatch(clearCosmetologicFeesByComestologist());
  }, [dispatch]);

  useEffect(() => {
    const { fortnightlyRange } = cosmetologicFeesByCosmetologistFilters;

    if (fortnightlyRange) {
      const [startDate, endDate] = fortnightlyRange.split(',');

      setPeriod(
        `${moment(startDate).format('DD/MM/YYYY')} - ${moment(endDate).format(
          'DD/MM/YYYY'
        )}`
      );
      return;
    }

    setPeriod(undefined);
  }, [cosmetologicFeesByCosmetologistFilters.fortnightlyRange]);

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <ExportButton onDownload={onDownload} disabled={!hasData} />
        <CosmetologicFeesByCosmetologistFilter />
      </div>

      <div ref={tableRef}>
        {hasData ? (
          <table
            style={{
              margin: '0 auto',
            }}
          >
            <ReportTableHeader
              user={fullname}
              role={role}
              title={cosmetologicFeesByCosmetologistReportConst.title}
              period={period}
              col1={4}
              col2={0}
              col3={8}
            />

            <tbody>
              <tr>
                {header?.map((item, key) => (
                  <th
                    key={key}
                    style={{ border: '1px solid black', padding: '15px' }}
                  >
                    {item}
                  </th>
                ))}
              </tr>

              {sales.map((row, key) => (
                <tr key={key}>
                  <td
                    style={{
                      border: '1px solid black',
                      textAlign: 'center',
                      padding: '15px',
                    }}
                  >
                    {row.orderNumber}
                  </td>
                  <td
                    style={{
                      border: '1px solid black',
                      textAlign: 'center',
                      padding: '15px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.branch}
                  </td>
                  <td
                    style={{
                      border: '1px solid black',
                      textAlign: 'center',
                      padding: '15px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {stringDate(row.date)}
                  </td>
                  <td
                    style={{
                      border: '1px solid black',
                      textAlign: 'center',
                      padding: '15px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {stringTime(row.time)}
                  </td>
                  <td
                    style={{
                      border: '1px solid black',
                      textAlign: 'center',
                      padding: '15px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.patient}
                  </td>
                  <td
                    style={{
                      border: '1px solid black',
                      textAlign: 'center',
                      padding: '15px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.product}
                  </td>
                  <td
                    style={{
                      border: '1px solid black',
                      textAlign: 'center',
                    }}
                  >
                    {formatPrice(Number(row.commission))}
                  </td>
                  <td
                    style={{
                      border: '1px solid black',
                      textAlign: 'center',
                      padding: '15px',
                    }}
                  >
                    {formatPrice(Number(row.salePrice))}
                  </td>
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr>
                <td
                  colSpan={7}
                  style={{
                    border: '1px solid black',
                    textAlign: 'right',
                    backgroundColor: '#e6e9eb',
                    padding: '15px',
                  }}
                >
                  <h4>TOTAL DE VENTA</h4>
                </td>
                <td
                  colSpan={2}
                  style={{
                    border: '1px solid black',
                    textAlign: 'center',
                    backgroundColor: '#e6e9eb',
                    padding: '5px',
                  }}
                >
                  {formatPrice(total)}
                </td>
              </tr>

              <tr>
                <td
                  colSpan={7}
                  style={{
                    border: '1px solid black',
                    textAlign: 'right',
                    backgroundColor: '#e6e9eb',
                    padding: '15px',
                  }}
                >
                  <h4>PORCENTAJE DE COMISIÓN</h4>
                </td>
                <td
                  colSpan={2}
                  style={{
                    border: '1px solid black',
                    textAlign: 'center',
                    backgroundColor: '#e6e9eb',
                    padding: '5px',
                  }}
                >
                  {globalPercent}%
                </td>
              </tr>

              <tr>
                <td
                  colSpan={7}
                  style={{
                    border: '1px solid black',
                    textAlign: 'right',
                    backgroundColor: '#e6e9eb',
                    padding: '15px',
                  }}
                >
                  <h4>TOTAL DE COMISIÓN</h4>
                </td>
                <td
                  colSpan={2}
                  style={{
                    border: '1px solid black',
                    textAlign: 'center',
                    backgroundColor: '#e6e9eb',
                    padding: '5px',
                  }}
                >
                  {formatPrice(totalCommissions)}
                </td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <DataNotFound />
        )}
      </div>
    </div>
  );
};